/**
 * 🔴 US-AIV2-0101 (@UC0171 @UC0176 · QĐ PO 23/09) — CẶP TRANG MƠ HỒ THẬT.
 *
 * ## Vì sao có file này
 *
 * Một số câu người Việt gõ **thật sự** ứng với hai trang, và ép một đáp án là dạy model **đoán thay
 * người dùng** — trái `@UC0171` (*"kẹt giữa đúng hai trang thì HỎI A hay B"*).
 *
 * Ca mở đầu, đo trên bản ghi thật: `"tôi đồng ý khoản này"` (×2, có dấu và không dấu).
 *
 *   `EXP-VOTE`    redirect · **mọi vai**              — bỏ phiếu cho khoản chi cần cả nhóm quyết
 *   `EXP-APPROVE` T2 · owner/admin/treasurer          — duyệt hoặc từ chối khoản chi
 *
 * Với **member**, `allowedPages` đã lọc `EXP-APPROVE` đi nên model buộc chọn `EXP-VOTE` — không có
 * nguy cơ 403. Mơ hồ chỉ tồn tại với **quản lý**, và với họ **cả hai đều hợp lệ**.
 *
 * ## 🔴 Đây KHÔNG phải cửa hậu để làm đẹp số
 *
 * Ba chốt, mỗi chốt có ca test riêng:
 *  1. **Cặp phải khai TRONG SỔ** — không suy từ độ giống mô tả, không đoán lúc chạy;
 *  2. **Quan hệ HAI CHIỀU** — khai một chiều là bẫy (ca A chấp nhận B mà ca B lại trượt A);
 *  3. **Chỉ cặp đã khai** — chọn một trang bất kỳ khác vẫn trượt y như cũ.
 *
 * Thêm một cặp là một **quyết định nội dung** của PO, không phải việc kỹ thuật: nó nói *"hai việc
 * này người dùng gọi giống nhau"*, và hệ quả là cổng thôi phân biệt chúng ở những câu đó.
 */

/**
 * Các cặp trang mà MỘT CÂU có thể ứng với cả hai — khai TAY, có chủ đích, PO duyệt.
 *
 * ⚠️ Cố ý **KHÔNG** suy tự động từ độ giống mô tả: hai trang giống nhau về chữ mà khác nhau về VIỆC
 * (vd `NOTIF-INBOX` đọc thông báo vs `ACCT-NOTIFY` cài đặt thông báo) thì mơ hồ ấy là **lỗi mô tả**,
 * phải sửa mô tả — không phải chấp nhận cả hai. Danh sách này chỉ dành cho mơ hồ **có thật trong
 * đời**, nơi hai việc khác nhau nhưng người dùng dùng chung một câu.
 */
const AMBIGUOUS_PAIRS: readonly (readonly [string, string])[] = Object.freeze([
  /*
   * "tôi đồng ý khoản này" — bỏ phiếu hay duyệt? Với quản lý, cả hai đều là việc họ làm được, và
   * câu không nói rõ. Người dùng phải chọn, không phải model đoán.
   */
  Object.freeze(["EXP-VOTE", "EXP-APPROVE"] as const),
]);

/** Bảng tra hai chiều, dựng một lần lúc nạp module. */
const PARTNERS: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const [a, b] of AMBIGUOUS_PAIRS) {
    if (!m.has(a)) m.set(a, []);
    if (!m.has(b)) m.set(b, []);
    m.get(a)!.push(b);
    m.get(b)!.push(a);
  }
  return new Map([...m].map(([k, v]) => [k, Object.freeze([...new Set(v)])]));
})();

/**
 * 🔴 review #936 MEDIUM-2 — HAI CỬA SỔ để chốt nhìn vào chính danh sách cặp.
 *
 * Docblock trên khai *"validator gác"*; đo thật thì `validate.ts` **không nhắc `ambiguous`** một lần
 * nào, và hai đột biến (cặp id BỊA · cặp trang có thật nhưng KHÁC VIỆC) đều cho 854/854 xanh. Một
 * lời khai trong chú thích không phải một phép đo.
 *
 * Danh sách nay **đọc được từ ngoài**, nên `ambiguous.test.ts` gác được hai điều mà văn xuôi chỉ
 * hứa: mọi id phải TỒN TẠI trong Sổ, và hai trang của một cặp phải CÙNG HỌ VIỆC.
 */
export function aiAmbiguousPairs(): readonly (readonly [string, string])[] {
  return AMBIGUOUS_PAIRS;
}

/** Mọi id xuất hiện trong danh sách cặp — để đối chiếu với Sổ. */
export function aiAmbiguousPageIds(): readonly string[] {
  return Object.freeze([...new Set(AMBIGUOUS_PAIRS.flat())]);
}

/** Trang nào có thể bị nhầm với `pageId` — rỗng nếu trang không khai cặp nào. */
export function ambiguousPartnersOf(pageId: string): readonly string[] {
  return PARTNERS.get(pageId) ?? [];
}

/**
 * Model chọn `chosen` cho một ca mà Sổ kỳ vọng `expected` — có chấp nhận được không?
 *
 * Đúng trang ⇒ có. Trang đã khai là cặp mơ hồ ⇒ có. **Mọi trang khác ⇒ KHÔNG**, y như trước.
 */
export function isAcceptablePageChoice(expected: string, chosen: string): boolean {
  if (expected === chosen) return true;
  return ambiguousPartnersOf(expected).includes(chosen);
}
